import { useRef } from 'react';
import styled from 'styled-components';
import FlexWrap from '@/components/common/FlexWrap';
import TableRow from '@/components/Receipt/TableRow';
import { ReceiptData } from '@/types/store.type';
import IMG_CUSTOMS from '@/assets/images/img_customs.png';
import { useAppSelector } from '@/hooks/useReduxHooks';
import { IGlobalState } from '@/store';
import {
  ICON_CAUTION,
  ICON_CLOSE_WHITE,
  IMG_DASHED_LINE,
  IMG_LOGO_KTP_BLACK,
  IMG_LOGO_KTP_BLUE,
  IMG_LOGO_SUCCESSMODE_BLACK,
  IMG_RECEIPT_FLAG,
  IMG_RECEIPT_GUIDE,
} from '@repo/assets';

type Props = {
  info: ReceiptData;
  index: number;
  style?: any;
  onClose: () => void;
};

function ReceiptImage({ info, index, style, onClose }: Props) {
  const receiptRef = useRef<HTMLDivElement | null>(null);
  const { nationality } = useAppSelector((state) => state) as IGlobalState;

  return (
    <ImageContainer dir='column' alignItems='center' style={style}>
      <GuideCustomsWrapper>
        <ButtonWrapper type='button' onClick={onClose}>
          <img src={ICON_CLOSE_WHITE} alt='닫기' width={24} />
        </ButtonWrapper>
        <GuideCustomsTitle>
          <img src={ICON_CAUTION} alt='' width={18} />
          {nationality === 'KOR'
            ? '세관 직원에게 보여주세요.'
            : nationality === 'CHN'
              ? '请给海关人员看看。'
              : 'Show this to Custom officers'}
        </GuideCustomsTitle>
        <GuideCustomsImg src={IMG_RECEIPT_GUIDE} alt='' />
      </GuideCustomsWrapper>
      <ReceiptContainer ref={receiptRef} className='notranslate'>
        <ContentBox>
          <LogoContainer gap='20px' alignItems='center'>
            <Logo src={IMG_LOGO_SUCCESSMODE_BLACK} />
            <Logo src={IMG_LOGO_KTP_BLACK} />
          </LogoContainer>
          {info?.barcodeS3Path && (
            <Barcode src={info.barcodeS3Path} style={{ top: 152 }} />
          )}
          <AdditionalInfo isSerialNumber>{info?.purchaseSn}</AdditionalInfo>
          <AdditionalInfo>
            KTP 환급전표
            <br />
            Korea Taxfree Payment receipt fot Tax Refund
            <br />
            https://ktaxpay.com/
            <br />
            (국세청인정서식 Authorized by Korea NTS)
          </AdditionalInfo>
          <RowWrap>
            {info.rowDataArr.map((item, idx) => (
              <TableRow key={idx} {...item} refundAfter={info.refundAfter} />
            ))}
          </RowWrap>
          {info.refundAfter && (
            <>
              <BlackGuideText>
                Pls present your passport, goods and receipt
              </BlackGuideText>
              <AdditionalInfo isSerialNumber>{info.purchaseSn}</AdditionalInfo>
              {info.barcodeS3Path && <BarcodeSection />}
              <DashedLine src={IMG_DASHED_LINE} />
            </>
          )}
          <GrayText>https://ktaxpay.com </GrayText>
          <FlexWrap justifyContent='space-evenly'>
            <GrayText>석세스모드</GrayText>
            <GrayText>제 138-00078 호</GrayText>
          </FlexWrap>
          <br />
          <GrayText>{`- ${index + 1} - `}</GrayText>
          <br />
          <br />
          {info.barcodeS3Path && (
            <Barcode src={info.barcodeS3Path} style={{ bottom: 324 }} />
          )}
        </ContentBox>
      </ReceiptContainer>
      <ReceiptEdge src={IMG_RECEIPT_FLAG} reverse />
    </ImageContainer>
  );
}

const ButtonWrapper = styled.button`
  display: block;
  margin-left: auto;
`;

const ImgClose = styled.img`
  width: 36px;
`;

const ImageContainer = styled(FlexWrap)`
  height: 80vh;
  margin-top: 20px;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const GuideCustomsWrapper = styled.div`
  width: 335px;
  padding: 16px 14px;
  background: #0b42ad;
`;
const GuideCustomsTitle = styled.strong`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 100%;
  color: #fff;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
`;

const GuideCustomsImg = styled.img`
  width: 100%;
`;

const ReceiptContainer = styled.div`
  width: 335px;
  -webkit-box-shadow: 0px 0px 12px 1px rgba(0, 0, 0, 0.2);
  box-shadow: 0px 0px 12px 1px rgba(0, 0, 0, 0.2);
  background-color: #fff;
`;
const ReceiptEdge = styled.img<{ reverse?: boolean }>`
  width: 335px;
  transform: ${({ reverse }) => reverse && 'rotate(180deg)'} translate(0, 1px);
`;
const ContentBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const LogoContainer = styled(FlexWrap)`
  margin: 40px 0 24px;
`;
const Logo = styled.img`
  width: 128px;
`;
const Barcode = styled.img`
  margin: 17px 0px 29px;
  max-width: 335px;
  height: auto;
`;
const BarcodeSection = styled.div`
  height: 80px;
`;
const AdditionalInfo = styled.p<{ isSerialNumber?: boolean }>`
  text-align: center;
  color: #5f6165;
  line-height: 22px;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: ${(props) => (props.isSerialNumber ? '10px' : '35px')};
`;
const RowWrap = styled.div`
  padding: 0 20px;
`;
const GrayText = styled.p`
  color: #5f6165;
  font-size: 14px;
  line-height: 22px;
  font-weight: 400;
  text-align: center;
`;
const DashedLine = styled.img`
  width: 100%;
  height: 1px;
  padding: 0 20px;
  margin-bottom: 16px;
`;
const BlackGuideText = styled.p`
  font-size: 14px;
  line-height: 22px;
  margin: 100px 0 10px;
  text-align: center;
`;
export default ReceiptImage;
